import { useEffect, useState } from 'react';
import * as R from 'ramda';

import { Progress } from '@atom/components/common/mui';
import { usePreferences } from '@atom/hooks/usePreferences';
import { AssetUsageSummary } from '@atom/types/assetUsage';
import { InventoryAssetDetailType } from '@atom/types/inventory';
import api from '@atom/utilities/api';
import { numberToLocaleString } from '@atom/utilities/currencyUtility';
import { INVENTORY_ASSETS_ENDPOINT } from '@atom/utilities/endpoints';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import classes from './cost.module.css';

interface Props {
  asset: InventoryAssetDetailType;
}

const CostHeader = ({ asset }: Props) => {
  const preferences = usePreferences();
  const fiscalStartDate = R.pathOr(null, ['fiscalStartDate'], preferences);
  const [summaryLoading, setSummaryLoading] = useState<boolean>(false);
  const [usageSummary, setUsageSummary] = useState<AssetUsageSummary>({
    totalUsage: 0,
    totalCost: 0,
    totalCostYear: 0,
    totalCostMonth: 0,
    totalUsageToday: 0,
    totalRemaining: 0,
    quantityOnHand: 0,
    rate: 0,
  });

  useEffect(() => {
    const getUsageSummary = async () => {
      setSummaryLoading(true);
      const { data } = await api.get(
        `${INVENTORY_ASSETS_ENDPOINT}/${asset.id}/usageSummary`,
      );

      setUsageSummary(data);
      setSummaryLoading(false);
    };

    if (asset.isMaterial) {
      getUsageSummary();
    }
  }, [asset.id, asset?.budget?.quantityOnHand]);

  const convertNumberToLocaleString = (input: string | number): string => {
    return Number(input).toLocaleString('en-US');
  };

  const showStockBasedSections = asset?.budget?.isStockBased;

  return (
    <div className={classes.rightPaneHeader}>
      {summaryLoading ? (
        <Progress size={20} />
      ) : (
        <>
          {showStockBasedSections && (
            <>
              <div className={classes.headerInfoBlock}>
                <div className={classes.headerSectionTitle}>
                  <span>Total Remaining (</span>
                  <span className={classes.usageUnit}>
                    {asset?.budget?.unit}
                  </span>
                  <span>)</span>
                </div>
                <div className={classes.headerSectionInfo}>
                  {convertNumberToLocaleString(usageSummary.totalRemaining)}
                </div>
              </div>
              <div className={classes.headerInfoBlock}>
                <div className={classes.headerSectionTitle}>
                  <span>Today's Usage (</span>
                  <span className={classes.usageUnit}>
                    {asset?.budget?.unit}
                  </span>
                  <span>)</span>
                </div>
                <div className={classes.headerSectionInfo}>
                  {convertNumberToLocaleString(usageSummary.totalUsageToday)}
                </div>
              </div>
            </>
          )}
          <div className={classes.headerInfoBlock}>
            <div className={classes.headerSectionTitle}>
              <span>Total Usage (</span>
              <span className={classes.usageUnit}>{asset?.budget?.unit}</span>
              <span>)</span>
            </div>
            <div className={classes.headerSectionInfo}>
              {convertNumberToLocaleString(usageSummary.totalUsage)}
            </div>
          </div>
          <div className={classes.headerInfoBlock}>
            <div className={classes.headerSectionTitle}>
              <span>Cost per </span>
              <span className={classes.usageUnit}>{asset?.budget?.unit}</span>
            </div>
            <div className={classes.headerSectionInfo}>
              {numberToLocaleString(usageSummary.rate)}
            </div>
          </div>
          <div className={classes.headerInfoBlock}>
            <div className={classes.headerSectionTitle}>
              {!isNilOrEmpty(fiscalStartDate)
                ? 'Total Cost (Fiscal Year)'
                : 'Total Cost (Year)'}
            </div>
            <div className={classes.headerSectionInfo}>
              {numberToLocaleString(usageSummary.totalCostYear)}
            </div>
          </div>
          <div className={classes.headerInfoBlock}>
            <div className={classes.headerSectionTitle}>Total Cost (Month)</div>
            <div className={classes.headerSectionInfo}>
              {numberToLocaleString(usageSummary.totalCostMonth)}
            </div>
          </div>
          <div className={classes.headerInfoBlock}>
            <div className={classes.headerSectionTitle}>Total Cost</div>
            <div className={classes.headerSectionInfo}>
              {numberToLocaleString(usageSummary.totalCost)}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CostHeader;
